import { initializeApp, type FirebaseApp, type FirebaseOptions } from 'firebase/app';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore, type Firestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';

const env = import.meta.env;
const useEmulators = env.DEV && env.VITE_USE_EMULATORS === 'true';

function readConfig(): FirebaseOptions {
  const apiKey = env.VITE_FIREBASE_API_KEY || (useEmulators ? 'demo-key' : undefined);
  const projectId = useEmulators ? 'demo-brandsorbyte' : env.VITE_FIREBASE_PROJECT_ID;
  if (!apiKey || !projectId) {
    throw new Error(
      'Falta la configuración de Firebase. Revisa web/.env.local (ver .env.example).',
    );
  }
  const options: FirebaseOptions = { apiKey, projectId };
  if (env.VITE_FIREBASE_AUTH_DOMAIN) options.authDomain = env.VITE_FIREBASE_AUTH_DOMAIN;
  if (env.VITE_FIREBASE_STORAGE_BUCKET) options.storageBucket = env.VITE_FIREBASE_STORAGE_BUCKET;
  if (env.VITE_FIREBASE_APP_ID) options.appId = env.VITE_FIREBASE_APP_ID;
  return options;
}

interface FirebaseClients {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  functions: Functions;
}

let clients: FirebaseClients | undefined;

/** Inicializa Firebase una sola vez; en desarrollo se conecta al Emulator Suite. */
export function firebase(): FirebaseClients {
  if (clients) return clients;
  const app = initializeApp(readConfig());
  const auth = getAuth(app);
  auth.languageCode = 'es-419';
  const db = getFirestore(app);
  const functions = getFunctions(app, 'us-central1');

  if (useEmulators) {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, '127.0.0.1', 8080);
    connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  }

  clients = { app, auth, db, functions };
  return clients;
}
